<template>
  <section>
    <Tabs>
      <TabsHeader tabs-header-class="atb-bg-white">
        <Tab
          v-for="(tab, index) in tabs"
          :key="'Tab' + tab.id"
          :index="index"
          :value="selectedTab"
          :closable="tab.name !== 'All Clients'"
          @tabClick="(index: number) => tabChange(index)"
          @close="(index: number) => closeTab(index, tab)"
        >
          {{ tab.name }}
          <sup v-if="getTabDirtyStatus(tab.id)">*</sup>
        </Tab>
      </TabsHeader>
      <TabsBody>
        <TabPanelItem
          v-for="(tab, index) in tabs"
          :key="tab.id"
        >
          <keep-alive
            :key="'keepAlive' + index"
            v-if="index === 0"
          >
            <ClientsList
              ref="clientsListRef"
              :key="'clientList' + tab.id"
              v-show="selectedTab === index"
              @create="openCreateFormTab"
              @edit="openEditFormTab"
              @deleteById="handleDeleteClient"
            />
          </keep-alive>
          <keep-alive
            :key="'keepAlive' + tab.id"
            v-else
          >
            <ClientDetails
              :key="'clientDetail' + tab.id"
              v-show="selectedTab === index"
              :current-client-id="tab.id"
              :tab-name="tab.name"
              @close-current-tab="closeCurrentTab"
              @deleteById="handleDeleteClient"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
  </section>
  <ClientsDelete
    @delete="deleteClient"
    :is-used="0"
    :entity="'Client'"
    ref="deleteModalRef"
  />
  <StreamingProgressModal
    :show="showDeleteProgressModal"
    :header-text="$t('deletingClient')"
    :message="deleteProgressMessage"
    :default-message="$t('clientDeletionMessage')"
    :percent="deleteProgressPercent"
    :is-closable="false"
    @on-modal-close="showDeleteProgressModal = false"
  />
</template>

<script setup lang="ts">
  import { nextTick, Ref, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import ClientsList from '@/app/features/admin/clients/components/ClientsList.vue';
  import { ModalActions } from '@/app/shared/types/modal-actions.interface';
  import { useClientStore } from '@/app/features/admin/clients/client-store';
  import clientApi, {
    type ClientDeletionProgressEvent,
  } from '@/app/features/admin/clients/client-service';
  import { useI18n } from 'vue-i18n';
  import { TabHeader } from '@/app/shared/types/tab-header.interface';
  import ClientsDelete from '@/app/features/ams/shared/DeleteModal.vue';
  import ClientDetails from '@/app/features/admin/clients/components/ClientDetails.vue';
  import { AdminAddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import StreamingProgressModal from '@/app/shared/components/StreamingProgressModal.vue';

  const { t } = useI18n();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();
  const clientsListRef = ref<InstanceType<typeof ClientsList>[]>([]);
  const { selectedTab, tabs } = storeToRefs(clientStore);
  const deleteModalRef: Ref<ModalActions | null> = ref(null);
  const clientIdToDelete = ref<number | string | null>(null);
  const showDeleteProgressModal = ref(false);
  const deleteProgressMessage = ref('');
  const deleteProgressPercent = ref(0);

  const getTabDirtyStatus = (clientId: string | number) => {
    let dirtyFlag = false;
    let dirtyTab = clientStore.clientDetails.get(clientId)?.dirtyTab;
    if (typeof dirtyTab !== 'undefined') {
      for (const [, dirtyTabValue] of dirtyTab ?? new Map().entries()) {
        if (dirtyTabValue) {
          dirtyFlag = true;
        }
      }
    }
    return dirtyFlag;
  };

  const tabChange = (index: number) => {
    selectedTab.value = index;
  };

  const closeCurrentTab = (index: number) => {
    clientStore.closeTab(index);
    clientsListRef.value[0]?.refreshClients();
    if (index === 1 && index <= selectedTab.value) {
      selectedTab.value -= 1;
      nextTick(() => {
        clientsListRef.value[0]?.refreshClients();
      });
    } else if (index <= selectedTab.value) {
      clientsListRef.value[0]?.refreshClients();
    }
  };

  const closeTab = (index: number, tab: TabHeader) => {
    clientStore.closeTab(tab.id);
  };

  const openCreateFormTab = async () => {
    await clientStore.createClientTab({
      id: -1,
      name: 'New Client',
      isNew: true,
      dirtyTab: new Map<string, boolean>(),
    });
  };

  const openEditFormTab = async ({
    id,
    name,
    isNew,
  }: AdminAddTabEvent): Promise<void> => {
    await clientStore.createClientTab({
      id: id,
      name: name,
      isNew: false,
      dirtyTab: new Map<string, boolean>(),
    });
  };

  const handleDeleteClient = (clientId: number | string) => {
    clientIdToDelete.value = clientId;
    deleteModalRef.value?.changeModalVisibility();
  };

  const deleteClient = async () => {
    if (clientIdToDelete.value == null) return;
    const idBeingDeleted = clientIdToDelete.value;

    showDeleteProgressModal.value = true;
    deleteProgressMessage.value = '';
    deleteProgressPercent.value = 0;

    try {
      await clientApi.streamDeleteClient(
        idBeingDeleted,
        (event: ClientDeletionProgressEvent) => {
          deleteProgressMessage.value = event.message;
          if (event.total > 0) {
            deleteProgressPercent.value = Math.round(
              (event.current / event.total) * 100
            );
          }
        }
      );
      deleteProgressPercent.value = 100;
      await clientStore.fetchClients();
      clientsListRef.value[0]?.refreshClients();
      closeCurrentTab(idBeingDeleted as number);
      alertStore.showNotification(t('clientDeleted'), t('Client'), Motif.SUCCESS);
    } catch (error: any) {
      alertStore.showNotification(
        error?.message || t('clientDeletionFailed'),
        t('Client'),
        Motif.ERROR
      );
    } finally {
      showDeleteProgressModal.value = false;
      deleteProgressMessage.value = '';
      deleteProgressPercent.value = 0;
    }
  };
</script>

<style scoped>
  .client-page {
    padding: 20px;
  }

  h1 {
    color: #333;
    margin-bottom: 16px;
  }
</style>
